import { ChevronLeft } from '@mui/icons-material';
import { Box, ButtonBase, Tooltip, Typography } from '@mui/material';
import {
  caseScheduleKeys,
  PickingStationEntity,
  useAndroid,
  usePickingStations,
} from '@peritasai/repository';
import { ErrorCard } from '@peritasai/ui-components';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PickingStationSelectionDialog } from '../../../shared';
import { usePickingStationClaim } from '../hooks';
import { useCurrentTime } from '../hooks/useCurrentTime';
import {
  TrayCardViewModel,
  useCaseCartDetailViewModel,
} from '../hooks/useCaseCartDetailViewModel';
import { readStoredStation, writeStoredStation } from '../utils/buildCartStorage';
import { getCountdown } from '../utils/countdown';
import { CartSummaryPanel } from './CartSummaryPanel';
import { LoadingCard } from './LoadingCard';
import { RemoveTrayConfirmationModal } from './RemoveTrayConfirmationModal';
import { Toast } from './Toast';
import { TrayDetailCard } from './TrayDetailCard';
import { popupOverlaySx } from './styles';

const BUILD_QUEUE_ROUTE = '/case-cart-assembly';

// Figma: this screen sits on a slightly darker canvas (#f4f4f4) than the
// app's default background (#fafafa) — a deliberate deviation, not an
// oversight, so this page owns its own container rather than reusing the
// shared page/content wrap variants in ./styles.
const pageSx = {
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '12px',
  padding: '24px',
  minHeight: 'calc(100vh - 80px)',
  backgroundColor: '#f4f4f4',
  position: 'relative' as const,
};

const backLinkSx = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '8px',
  padding: '4px 0',
  width: 'fit-content',
  cursor: 'pointer',
  color: '#4652e2',
};

const backLinkTextSx = {
  color: '#4652e2',
  fontSize: '11.6px',
  fontWeight: 700,
};

const headerRowSx = {
  display: 'flex',
  justifyContent: 'space-between',
  width: '100%',
  gap: '16px',
};

const caseTextGroupSx = {
  minWidth: 0,
  flex: 1,
};

const caseMetaSx = {
  color: '#64748b',
  fontSize: '12px',
  fontWeight: 600,
  textTransform: 'uppercase' as const,
};

// fontSize shrinks below the tablet's designed width (e.g. a smaller/scaled
// device) so the full case time + surgeon name has a better chance of
// fitting before the ellipsis engages, instead of overflowing the row.
const caseTitleSx = {
  color: '#121b2a',
  fontSize: { xs: '20px', sm: '24px', md: '32px' },
  fontWeight: 700,
  letterSpacing: '-0.32px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap' as const,
};

const caseSubSx = {
  color: '#64748b',
  fontSize: '12px',
  fontWeight: 400,
};

const stageBadgeSx = {
  backgroundColor: '#121b2a',
  borderRadius: '8px',
  padding: '16px 8px',
  width: '155px',
  flexShrink: 0,
  textAlign: 'center' as const,
};

const stageLabelSx = {
  color: '#64748b',
  fontSize: '12px',
  fontWeight: 600,
};

const stageValueSx = {
  color: '#fff',
  fontSize: '32px',
  fontWeight: 600,
  letterSpacing: '-2.24px',
};

// flex-start, not Figma's items-center: our tray cards are taller than
// Figma's (222px vs 190px, see cardSx), so centering would push the
// shorter CartSummaryPanel down instead of flush with the cards' tops.
const contentRowSx = {
  display: 'flex',
  gap: '16px',
  alignItems: 'flex-start',
};

const trayGridSx = {
  flex: 1,
  // minWidth: 0 overrides the flex item's default min-width:auto, and
  // minmax(0, 1fr) overrides the grid track's default minmax(auto, 1fr) —
  // without both, a card with long unbreakable content (e.g. a real
  // location name like "ROBOTIC RACK 1 - SHELVES 1 TO 3") grows past its
  // fair share, blowing out this row and pushing CartSummaryPanel off
  // the tablet's screen instead of letting the card's own text truncate.
  minWidth: 0,
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '16px',
};

type CaseCartDetailPageProps = {
  caseId: string;
};

export const CaseCartDetailPage = ({ caseId }: CaseCartDetailPageProps) => {
  const { t } = useTranslation(['periverse', 'common']);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const now = useCurrentTime(60_000);
  const [pendingRemoval, setPendingRemoval] = useState<TrayCardViewModel | null>(
    null
  );
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Picking-station selection (PVS-2593), reusing the same logic as
  // BuildCartPage: the android bridge resolves a station automatically when
  // available, otherwise the cart builder picks one manually. Scanning must
  // happen at the picking station's location, not the case's own OR location.
  const { locationId: androidLocationId } = useAndroid();
  const [manualStation, setManualStation] = useState<PickingStationEntity | null>(
    () => readStoredStation()
  );
  const [previewStationId, setPreviewStationId] = useState<string | null>(null);
  const effectiveLocationId = androidLocationId ?? manualStation?.locationId ?? null;
  const { claimStation, releaseClaimedStation } = usePickingStationClaim();

  // The card list page relies on SSE to pick up tray/status changes live, but
  // it may have missed the event while this page was open (or the card list
  // wasn't mounted at all) — e.g. after a tray Remove, which only invalidates
  // this page's own detail query, not the list's. Invalidate the list query
  // on every way of leaving this page so it re-fetches fresh data instead of
  // serving a stale pre-change snapshot — same fix already applied for the
  // legacy build-cart route (see invalidateCaseScheduleQueries there), just
  // not carried over here.
  const returnToBuildQueue = useCallback(() => {
    releaseClaimedStation();
    void queryClient.invalidateQueries({ queryKey: caseScheduleKeys.lists() });
    navigate({ to: BUILD_QUEUE_ROUTE });
  }, [navigate, queryClient, releaseClaimedStation]);

  const handleBack = returnToBuildQueue;
  const handleReserveSettled = returnToBuildQueue;

  const handleRemoveSuccess = useCallback(() => {
    setToastMessage(t('pages.caseCartAssembly.progressScreen.removeSuccessToast'));
  }, [t]);

  const vm = useCaseCartDetailViewModel({
    caseId,
    locationId: effectiveLocationId,
    onReserveSettled: handleReserveSettled,
    onRemoveSuccess: handleRemoveSuccess,
  });

  // Once the case's cart already has a resolved location, it's already built —
  // there's nothing left to scan, so don't force a picking-station pick just
  // to show the read-only view. Wait for the case to finish loading first —
  // vm.caseEntity is undefined until then, which would otherwise read as "no
  // location yet" and fire the picking-stations fetch prematurely.
  const needsStationSelection =
    !vm.isLoading && !effectiveLocationId && !vm.caseEntity?.selectedCart?.location;

  const {
    data: pickingStations,
    isLoading: isPickingStationsLoading,
    isError: isPickingStationsError,
    refetch: refetchPickingStations,
  } = usePickingStations(needsStationSelection);

  const handleConfirmStation = useCallback(
    (stationId: string) => {
      const station = pickingStations?.find((s) => s.id === stationId);
      if (!station) return;

      writeStoredStation(station);
      setManualStation(station);
      // Claims the hub (marks it unavailable) so another cart builder can't
      // pick the same one.
      claimStation(stationId);
    },
    [pickingStations, claimStation]
  );

  const [prevIsError, setPrevIsError] = useState(vm.isError);
  const [isErrorDismissed, setIsErrorDismissed] = useState(false);
  if (vm.isError !== prevIsError) {
    setPrevIsError(vm.isError);
    if (vm.isError) setIsErrorDismissed(false);
  }

  const handleRemoveRequested = useCallback((card: TrayCardViewModel) => {
    setPendingRemoval(card);
  }, []);

  const handleRemoveCancelled = useCallback(() => {
    setPendingRemoval(null);
  }, []);

  const handleRemoveConfirmed = useCallback(() => {
    if (!pendingRemoval) return;
    vm.handleRemoveConfirmed(pendingRemoval);
    setPendingRemoval(null);
  }, [pendingRemoval, vm]);

  const caseEntity = vm.caseEntity;
  const countdown = caseEntity ? getCountdown(caseEntity.dateTime, now) : null;

  return (
    <Box sx={pageSx} data-testid="case-cart-detail-page">
      {vm.isLoading && (
        <LoadingCard rows={5} titleWidth="35%" cardWidth="100%" cardMaxWidth="100%" />
      )}

      {!vm.isLoading && vm.isError && !isErrorDismissed && (
        <Box sx={popupOverlaySx}>
          <ErrorCard
            title={t('pages.buildCart.caseList.errorTitle')}
            description={t('pages.buildCart.caseList.errorDescription')}
            retryLabel={t('common:tryAgain')}
            onRetry={() => void vm.refetch()}
            cancelLabel={t('common:cancel')}
            onCancel={() => setIsErrorDismissed(true)}
          />
        </Box>
      )}

      {!vm.isLoading && (!vm.isError || isErrorDismissed) && caseEntity && (
        <>
          <ButtonBase
            sx={backLinkSx}
            data-testid="back-to-build-queue"
            onClick={handleBack}
          >
            <ChevronLeft sx={{ fontSize: '16px' }} />
            <Typography sx={backLinkTextSx}>
              {t('pages.caseCartAssembly.progressScreen.backToBuildQueueLabel')}
            </Typography>
          </ButtonBase>

          <Box sx={headerRowSx}>
            <Box sx={caseTextGroupSx}>
              <Typography sx={caseMetaSx}>
                {[caseEntity.floor, caseEntity.room, caseEntity.caseHospitalId]
                  .filter(Boolean)
                  .join(' • ')}
              </Typography>
              <Tooltip title={`${caseEntity.getFormattedTime()} • ${caseEntity.surgeon}`}>
                <Typography sx={caseTitleSx}>
                  {caseEntity.getFormattedTime()} • {caseEntity.surgeon}
                </Typography>
              </Tooltip>
              <Typography sx={caseSubSx}>{caseEntity.caseType}</Typography>
            </Box>
            <Box sx={stageBadgeSx} data-testid="stage-badge">
              <Typography sx={stageLabelSx}>
                {vm.isComplete
                  ? t('pages.caseCartAssembly.stagedLabel')
                  : t('pages.caseCartAssembly.stageWithinLabel')}
              </Typography>
              <Typography sx={stageValueSx}>
                {vm.isComplete
                  ? t('pages.caseCartAssembly.completedLabel')
                  : countdown
                    ? t('pages.caseCartAssembly.countdownLabel', {
                        hours: countdown.hours,
                        minutes: countdown.minutes,
                      })
                    : ''}
              </Typography>
            </Box>
          </Box>

          {needsStationSelection && (
            <>
              {isPickingStationsLoading && (
                <Box sx={popupOverlaySx}>
                  <LoadingCard rows={3} titleWidth="45%" cardMaxWidth="480px" />
                </Box>
              )}

              {!isPickingStationsLoading && isPickingStationsError && (
                <Box sx={popupOverlaySx}>
                  <ErrorCard
                    title={t('pages.buildCart.selectStation.errorTitle')}
                    description={t('pages.buildCart.selectStation.errorDescription')}
                    retryLabel={t('common:tryAgain')}
                    onRetry={() => void refetchPickingStations()}
                    cancelLabel={t('common:cancel')}
                    onCancel={handleBack}
                  />
                </Box>
              )}

              {!isPickingStationsLoading && !isPickingStationsError && (
                <PickingStationSelectionDialog
                  open
                  stations={pickingStations ?? []}
                  selectedStationId={previewStationId}
                  onSelectedStationChange={setPreviewStationId}
                  onSelect={handleConfirmStation}
                  onCancel={handleBack}
                />
              )}
            </>
          )}

          {!needsStationSelection && (
            <Box sx={contentRowSx}>
              <Box sx={trayGridSx} data-testid="tray-grid">
                {vm.trayCards.map((card) => (
                  <TrayDetailCard
                    key={card.checklistItemId}
                    card={card}
                    onRemove={handleRemoveRequested}
                    removeDisabled={caseEntity.checkedOutStatus}
                  />
                ))}
              </Box>
              <CartSummaryPanel
                cartReferenceNumber={vm.cartReferenceNumber}
                onCartCount={vm.onCartCount}
                pendingCount={vm.pendingCount}
                isComplete={vm.isComplete}
                onPrimaryAction={vm.handleSave}
                disabled={!vm.hasUnsavedChanges}
              />
            </Box>
          )}
        </>
      )}

      <RemoveTrayConfirmationModal
        open={!!pendingRemoval}
        trayName={pendingRemoval?.name ?? ''}
        trayLocation={pendingRemoval?.locationDisplay ?? null}
        onCancel={handleRemoveCancelled}
        onConfirm={handleRemoveConfirmed}
      />

      <Toast
        open={!!toastMessage}
        message={toastMessage ?? ''}
        onClose={() => setToastMessage(null)}
      />
    </Box>
  );
};
